/**
 * The rich text of a piece. The API sends `blocks` — [{ type, html }] with an
 * html already sanitized on the server to strong, em and br (and li inside a
 * list) — and keeps `paragraphs` as plain text for pieces that have no blocks.
 * The html is tokenized here, never injected into the page.
 */
export const BLOCK_PARAGRAPH = 'paragraph';
export const BLOCK_QUOTE = 'quote';
export const BLOCK_HEADING = 'heading';
export const BLOCK_LIST = 'list';

const KNOWN_TYPES = [BLOCK_PARAGRAPH, BLOCK_QUOTE, BLOCK_HEADING, BLOCK_LIST];
const TOKEN_PATTERN = /<(\/?)(strong|em|li)>|<br\s*\/?>|<[^>]*>|([^<]+)/g;
const LINE_BREAK = '\n';
const ENTITIES = [[/&lt;/g, '<'], [/&gt;/g, '>'], [/&quot;/g, '"'], [/&#39;/g, "'"], [/&nbsp;/g, ' '], [/&amp;/g, '&']];

const decodeEntities = (text) => ENTITIES.reduce((result, [pattern, value]) => result.replace(pattern, value), text);

/** Splits an html into items (one per <li>, or a single one) made of runs { text, bold, italic }. */
const parseItems = (html) => {
  const items = [];
  let runs = [];
  const marks = { strong: false, em: false };
  const closeItem = () => {
    if (runs.length) items.push(runs);
    runs = [];
  };
  String(html || '').replace(TOKEN_PATTERN, (token, closing, tag, text) => {
    if (text !== undefined) runs.push({ text: decodeEntities(text), bold: marks.strong, italic: marks.em });
    else if (tag === 'li') closeItem();
    else if (tag) marks[tag] = !closing;
    else if (/^<br/i.test(token)) runs.push({ text: LINE_BREAK, bold: false, italic: false });
    return '';
  });
  closeItem();
  return items;
};

const plainItem = (text) => [{ text, bold: false, italic: false }];

/** Blocks of a normalized piece: its own blocks or, without them, one block per paragraph. */
export const pieceBlocks = (piece) => {
  const blocks = Array.isArray(piece && piece.blocks) ? piece.blocks : [];
  if (blocks.length) {
    return blocks
      .filter((block) => KNOWN_TYPES.includes(block.type))
      .map((block) => ({ type: block.type, items: parseItems(block.html) }));
  }
  return ((piece && piece.parrafos) || []).map((text) => ({ type: BLOCK_PARAGRAPH, items: [plainItem(text)] }));
};

const isFormatted = (runs) => runs.some((run) => run.bold || run.italic);

/**
 * Applies a text rule (the personalization) to an item. An item without
 * formatting is resolved as one whole text, exactly like a plain paragraph;
 * with formatting, each run is resolved on its own so the marks survive.
 */
const resolveItem = (runs, resolve) => {
  if (!isFormatted(runs)) {
    const text = resolve(runs.map((run) => run.text).join(''));
    return text.trim() ? plainItem(text) : [];
  }
  const resolved = runs
    .map((run) => (run.text === LINE_BREAK ? run : { ...run, text: resolve(run.text) }))
    .filter((run) => run.text !== '');
  return resolved.some((run) => run.text.trim()) ? resolved : [];
};

/** Resolves every block and drops the ones left empty (a greeting without a name, for example). */
export const resolveBlocks = (blocks, resolve) =>
  blocks
    .map((block) => ({ ...block, items: block.items.map((runs) => resolveItem(runs, resolve)).filter((runs) => runs.length) }))
    .filter((block) => block.items.length);
